import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  getGovernanceFailure,
  getGovernanceSuccess,
  loadGovernanceVersionFailure,
  loadGovernanceVersionSuccess,
  saveGovernanceFailure,
  saveGovernanceSuccess,
  getResidentGovernanceFailure,
  getResidentGovernanceSuccess,
  updateGovernanceStatusFailure,
  updateGovernanceStatusSuccess,
  completeGovernanceItemSuccess,
  completeGovernanceItemFailure,
  dismissGovernanceItemSuccess,
  dismissGovernanceItemFailure,
  snoozeGovernanceItemSuccess,
  snoozeGovernanceItemFailure,
} from "./actions";
import {
  GET_GOVERNANCE,
  LOAD_GOVERNANCE_VERSION,
  SAVE_GOVERNANCE,
  GET_RESIDENT_GOVERNANCE,
  UPDATE_GOVERNANCE_STATUS,
  COMPLETE_GOVERNANCE_ITEM,
  DISMISS_GOVERNANCE_ITEM,
  MUTE_GOVERNANCE_ITEM,
  SNOOZE_GOVERNANCE_ITEM,
} from "./constants";

export function* getGovernanceSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/governance?${query}`,
      payload,
    );
    yield put(getGovernanceSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getGovernanceFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching governance!"));
  }
}

export function* saveGovernanceSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/governance/add`,
      payload,
    );
    yield put(saveGovernanceSuccess(data));
    typeof onSuccess === "function" && onSuccess();
    yield put(successAlert("Governance saved successfully"));
  } catch (e) {
    yield put(saveGovernanceFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching data!"));
  }
}

export function* loadGovernanceVersionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).get, `/api/governance/${payload}`);
    yield put(loadGovernanceVersionSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(loadGovernanceVersionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while loading governance version!"));
  }
}

export function* getResidentGovernanceSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/governance-item?${query}`,
    );
    yield put(getResidentGovernanceSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getResidentGovernanceFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching resident governance!"));
  }
}

export function* updateGovernanceStatusSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, itemId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/governance-item/${itemId}/status`,
      formData,
    );
    yield put(updateGovernanceStatusSuccess(data));
    typeof onSuccess === "function" && onSuccess();
    yield put(successAlert("Status updated successfully"));
  } catch (e) {
    yield put(updateGovernanceStatusFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating governance status!"));
  }
}

export function* completeGovernanceItemSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, itemId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/governance-item/${itemId}/complete`,
      formData,
    );
    yield put(completeGovernanceItemSuccess(data));
    typeof onSuccess === "function" && onSuccess();
    yield put(successAlert("Item marked as complete"));
  } catch (e) {
    yield put(completeGovernanceItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while completing governance item!"));
  }
}

export function* getGovernanceFlow() {
  yield takeEvery(GET_GOVERNANCE, getGovernanceSaga);
}
export function* saveGovernanceFlow() {
  yield takeEvery(SAVE_GOVERNANCE, saveGovernanceSaga);
}
export function* loadGovernanceVersionFlow() {
  yield takeEvery(LOAD_GOVERNANCE_VERSION, loadGovernanceVersionSaga);
}
export function* getResidentGovernanceFlow() {
  yield takeEvery(GET_RESIDENT_GOVERNANCE, getResidentGovernanceSaga);
}
export function* updateGovernanceStatusFlow() {
  yield takeEvery(UPDATE_GOVERNANCE_STATUS, updateGovernanceStatusSaga);
}
export function* completeGovernanceItemFlow() {
  yield takeEvery(COMPLETE_GOVERNANCE_ITEM, completeGovernanceItemSaga);
}

// Dismiss with a reason (and optionally set-as-baseline → durable ClinicalBaseline).
export function* dismissGovernanceItemSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, itemId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/governance-item/${itemId}/dismiss`,
      formData,
    );
    yield put(dismissGovernanceItemSuccess(data));
    typeof onSuccess === "function" && onSuccess();
    yield put(
      successAlert(
        formData?.set_as_baseline
          ? "Accepted as a baseline."
          : "Alert dismissed.",
      ),
    );
  } catch (e) {
    yield put(dismissGovernanceItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while dismissing alert!"));
  }
}

// Mute — turn this resident's alerts of this kind off for a while (or until
// turned back on); dismisses this alert as "muted".
export function* muteGovernanceItemSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, itemId, formData } = payload;
  try {
    yield call(
      api(token).post,
      `/api/resident/${residentId}/governance-item/${itemId}/mute`,
      formData,
    );
    typeof onSuccess === "function" && onSuccess();
    yield put(successAlert("Alerts muted."));
  } catch (e) {
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while muting alerts!"));
  }
}

// "Check later" — snooze until a date; the alert auto-reopens for review.
export function* snoozeGovernanceItemSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, itemId, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/governance-item/${itemId}/snooze`,
      formData,
    );
    yield put(snoozeGovernanceItemSuccess(data));
    typeof onSuccess === "function" && onSuccess();
    yield put(successAlert("Set to monitor — we'll bring it back for review."));
  } catch (e) {
    yield put(snoozeGovernanceItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while setting alert to monitor!"));
  }
}

export function* dismissGovernanceItemFlow() {
  yield takeEvery(DISMISS_GOVERNANCE_ITEM, dismissGovernanceItemSaga);
}
export function* muteGovernanceItemFlow() {
  yield takeEvery(MUTE_GOVERNANCE_ITEM, muteGovernanceItemSaga);
}
export function* snoozeGovernanceItemFlow() {
  yield takeEvery(SNOOZE_GOVERNANCE_ITEM, snoozeGovernanceItemSaga);
}

export default function* ResidentGovernanceSaga() {
  yield all([
    getGovernanceFlow(),
    saveGovernanceFlow(),
    loadGovernanceVersionFlow(),
    getResidentGovernanceFlow(),
    updateGovernanceStatusFlow(),
    completeGovernanceItemFlow(),
    dismissGovernanceItemFlow(),
    muteGovernanceItemFlow(),
    snoozeGovernanceItemFlow(),
  ]);
}
